import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    alpha,
    useTheme,
    Skeleton,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    Container,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Chip,
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    GridView as GridViewIcon,
    ViewList as ListViewIcon,
    Bolt as BoltIcon,
    TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { trendingApi } from '../../services/api';
import PageAlert from '../PageAlert';
import { COUNTRIES, PLATFORMS } from './constants';
import TrendCard from './TrendCard';
import TrendRow  from './TrendRow';

// Platform accent colours
const PLATFORM_ACCENT = {
    web:       '#4285F4',
    youtube:   '#FF0000',
    instagram: '#E1306C',
    facebook:  '#1877F2',
};

// Skeleton cards while loading
function SkeletonGrid() {
    return (
        <>
            {Array.from({ length: 8 }).map((_, i) => (
                <Grid key={i} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                    <Box sx={{ borderRadius: '16px', overflow: 'hidden', border: '1px solid rgba(0,0,0,0.06)' }}>
                        <Skeleton variant="rectangular" sx={{ aspectRatio: '16/9', width: '100%' }} />
                        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Skeleton variant="rounded" width={60}  height={20} />
                                <Skeleton variant="rounded" width={50}  height={20} />
                            </Box>
                            <Skeleton variant="text" sx={{ fontSize: '1.1rem' }} />
                            <Skeleton variant="text" sx={{ fontSize: '1.1rem', width: '75%' }} />
                            <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                <Skeleton variant="rounded" height={44} />
                                <Skeleton variant="rounded" height={44} />
                            </Box>
                        </Box>
                    </Box>
                </Grid>
            ))}
        </>
    );
}

function SkeletonList() {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {Array.from({ length: 10 }).map((_, i) => (
                <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: '12px' }} />
            ))}
        </Box>
    );
}

// Stats bar above the grid
function StatsBar({ trends, platform, accent }) {
    const withTraffic   = trends.filter(t => t.traffic).length;
    const withImages    = trends.filter(t => t.picture_url).length;
    const withNews      = trends.filter(t => t.news?.length).length;
    const topTraffic    = trends.find(t => t.traffic)?.traffic || '';

    const stats = [
        { label: 'Topics',       value: trends.length },
        ...(withTraffic  ? [{ label: 'With search volume', value: withTraffic  }] : []),
        ...(withImages   ? [{ label: 'With thumbnails',    value: withImages   }] : []),
        ...(withNews     ? [{ label: 'With news',          value: withNews     }] : []),
        ...(topTraffic   ? [{ label: 'Top searches',       value: topTraffic   }] : []),
    ];

    return (
        <Box sx={{
            display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3,
            px: 2.5, py: 1.5,
            borderRadius: '14px',
            bgcolor: alpha(accent, 0.04),
            border: `1px solid ${alpha(accent, 0.12)}`,
        }}>
            {stats.map((s) => (
                <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
                    <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: accent }}>
                        {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary', fontWeight: 600 }}>
                        {s.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
}

const TrendingNow = () => {
    const theme    = useTheme();
    const [trends,   setTrends]   = useState([]);
    const [loading,  setLoading]  = useState(true);
    const [region,   setRegion]   = useState('united_states');
    const [platform, setPlatform] = useState('web');
    const [error,    setError]    = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' | 'list'
    const [lastUpdated, setLastUpdated] = useState(null);

    const accent = PLATFORM_ACCENT[platform] || theme.palette.primary.main;

    const fetchTrends = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await trendingApi.getTrending(region, platform);
            setTrends(data || []);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Failed to fetch trends:', err);
            setError('Could not load trending data. Please try again in a moment.');
        } finally {
            setLoading(false);
        }
    }, [region, platform]);

    useEffect(() => { fetchTrends(); }, [fetchTrends]);

    const currentPlatform = PLATFORMS.find(p => p.code === platform);

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>

            {/* ── Header ── */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', lg: 'flex-end' },
                gap: 3, mb: 5,
            }}>
                <Box sx={{ maxWidth: { lg: '50%' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Chip
                            icon={<BoltIcon sx={{ fontSize: '14px !important' }} />}
                            label="LIVE INTELLIGENCE"
                            size="small"
                            sx={{
                                height: 24, fontSize: '0.65rem', fontWeight: 900,
                                letterSpacing: '0.08em',
                                bgcolor: alpha(accent, 0.12), color: accent,
                                border: `1px solid ${alpha(accent, 0.25)}`,
                                '& .MuiChip-icon': { color: accent },
                            }}
                        />
                        {lastUpdated && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600 }}>
                                Updated {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                        )}
                    </Box>
                    <Typography
                        variant="h2"
                        sx={{
                            fontWeight: 900, letterSpacing: '-0.04em',
                            mb: 1, fontSize: { xs: '2rem', md: '3rem' },
                            background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${alpha(accent, 0.85)} 100%)`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                        }}
                    >
                        Market Trends
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.65, maxWidth: 520 }}>
                        Real-time signals from{' '}
                        <Box component="span" sx={{ fontWeight: 800, color: accent }}>
                            {currentPlatform?.name || 'Google Search'}
                        </Box>
                        {' '}— spot what's viral before you post.
                    </Typography>
                </Box>

                {/* Controls */}
                <Box sx={{
                    display: 'flex', flexWrap: 'wrap', gap: 1.5,
                    alignItems: 'center',
                    width: { xs: '100%', lg: 'auto' },
                    justifyContent: { xs: 'flex-start', lg: 'flex-end' },
                }}>
                    {/* Platform tabs */}
                    <ToggleButtonGroup
                        value={platform}
                        exclusive
                        onChange={(_, next) => next && setPlatform(next)}
                        sx={{
                            bgcolor: alpha(theme.palette.background.paper, 0.6),
                            borderRadius: '14px', p: 0.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                            '& .MuiToggleButton-root': {
                                border: 'none', borderRadius: '10px !important',
                                px: { xs: 1.25, sm: 2 }, py: 0.9,
                                fontSize: '0.8rem', fontWeight: 700,
                                color: 'text.secondary', textTransform: 'none', gap: 0.75,
                                '&.Mui-selected': {
                                    bgcolor: PLATFORM_ACCENT[platform] || theme.palette.primary.main,
                                    color: '#fff',
                                    '&:hover': { filter: 'brightness(1.05)' },
                                },
                                '&:hover': { bgcolor: alpha(accent, 0.06) },
                            },
                        }}
                    >
                        {PLATFORMS.map((p) => (
                            <ToggleButton key={p.code} value={p.code}
                                sx={p.code === platform ? { bgcolor: `${PLATFORM_ACCENT[p.code]} !important` } : {}}
                            >
                                <span style={{ fontSize: '1rem' }}>{p.icon}</span>
                                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{p.name}</Box>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>

                    {/* Region + View + Refresh */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <FormControl
                            variant="standard"
                            sx={{
                                minWidth: 160,
                                '& .MuiInput-root': {
                                    px: 1.5, py: 0.75,
                                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                                    borderRadius: '12px',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    '&:before, &:after': { display: 'none' },
                                    '&:hover': { borderColor: alpha(accent, 0.3) },
                                },
                            }}
                        >
                            <Select
                                value={region}
                                onChange={(e) => setRegion(e.target.value)}
                                renderValue={(selected) => {
                                    const c = COUNTRIES.find(c => c.code === selected);
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <span style={{ fontSize: '1.1rem' }}>{c?.flag}</span>
                                            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{c?.name}</Typography>
                                        </Box>
                                    );
                                }}
                                MenuProps={{
                                    PaperProps: { sx: { borderRadius: 3, mt: 1, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }
                                }}
                            >
                                {COUNTRIES.map((c) => (
                                    <MenuItem key={c.code} value={c.code} sx={{ py: 1.25, mx: 0.5, borderRadius: 1.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            <span style={{ fontSize: '1.1rem' }}>{c.flag}</span>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.88rem' }}>{c.name}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Grid / List toggle */}
                        <ToggleButtonGroup
                            value={viewMode}
                            exclusive
                            onChange={(_, next) => next && setViewMode(next)}
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.background.paper, 0.8),
                                borderRadius: '12px', border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                '& .MuiToggleButton-root': {
                                    border: 'none', borderRadius: '10px !important',
                                    p: 1,
                                    '&.Mui-selected': { bgcolor: alpha(accent, 0.12), color: accent },
                                },
                            }}
                        >
                            <ToggleButton value="grid"><GridViewIcon sx={{ fontSize: 18 }} /></ToggleButton>
                            <ToggleButton value="list"><ListViewIcon sx={{ fontSize: 18 }} /></ToggleButton>
                        </ToggleButtonGroup>

                        <Tooltip title="Refresh trends">
                            <IconButton
                                onClick={fetchTrends}
                                disabled={loading}
                                sx={{
                                    p: 1.25, borderRadius: '12px',
                                    bgcolor: alpha(accent, 0.08), color: accent,
                                    '&:hover': { bgcolor: alpha(accent, 0.16) },
                                    '& svg': loading ? { animation: 'spin 1s linear infinite' } : {},
                                    '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } },
                                }}
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Box>

            {error && <PageAlert error={error} />}

            {/* Stats bar */}
            {!loading && trends.length > 0 && (
                <StatsBar trends={trends} platform={platform} accent={accent} />
            )}

            {/* Content */}
            {loading ? (
                viewMode === 'grid'
                    ? <Grid container spacing={2.5}><SkeletonGrid /></Grid>
                    : <SkeletonList />
            ) : viewMode === 'grid' ? (
                <Grid container spacing={2.5}>
                    {trends.map((item, index) => (
                        <TrendCard key={index} item={item} index={index} accent={accent} />
                    ))}
                </Grid>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {trends.map((item, index) => (
                        <TrendRow key={index} item={item} index={index} accent={accent} />
                    ))}
                </Box>
            )}

            {/* Footer */}
            {!loading && (
                <Box sx={{
                    mt: 8, p: 4, borderRadius: '20px',
                    bgcolor: alpha(accent, 0.03),
                    border: `1px solid ${alpha(accent, 0.08)}`,
                    display: 'flex', flexDirection: { xs: 'column', md: 'row' },
                    alignItems: 'center', gap: 3,
                    textAlign: { xs: 'center', md: 'left' },
                }}>
                    <Box sx={{
                        width: 64, height: 64, borderRadius: '18px', flexShrink: 0,
                        bgcolor: alpha(accent, 0.12),
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <TrendingUpIcon sx={{ fontSize: 32, color: accent }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5, letterSpacing: '-0.02em' }}>
                            Tap any trend to open the source · Click{' '}
                            <Box component="span" sx={{
                                display: 'inline-flex', alignItems: 'center', gap: 0.4,
                                px: 0.75, py: 0.1, borderRadius: '6px',
                                bgcolor: alpha(accent, 0.12), color: accent, fontSize: '0.9rem',
                            }}>✏ Create</Box>{' '}
                            to pre-fill a post
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>
                            Trending topics typically see <strong>3–5× higher</strong> organic reach in the first 24 hours.
                            Switch platforms and regions to find cross-platform opportunities.
                        </Typography>
                    </Box>
                </Box>
            )}
        </Container>
    );
};

export default TrendingNow;
